import { FormApi } from "final-form";
import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { EmteriaRoles } from "src/data/roles";
import i18next from "src/i18n/config";
import { useDispatch } from "src/store";
import { withRequiredRole } from "src/ui/navigation/utils/withRequiredRole";
import { DangerButton, PrimaryButton } from "src/ui/shared/CustomButton";
import { NumberField } from "src/ui/shared/form/NumberField";
import { TextField } from "src/ui/shared/form/TextField";
import { showErrorToast, showSuccessToast } from "src/ui/shared/toasts/Toasts";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";

import { updateGroupMembers } from "@dashboard/devices/store";
import { PageContainer } from "@dashboard/shared/components/PageContainer";
import { SectionContainer, SectionHeader } from "@dashboard/shared/styles";
import { useUpdateWorkspaceMembersMutation } from "@dashboard/workspaces/api";
import { Box, Theme } from "@mui/material";
import { withMetaDescription } from "@navigation/utils/withMetaDescription";
import { ControlledSelectField } from "@shared/form/SelectField/SelectField";

type FormValues = {
    id: number;
    email: string;
    target: "workspace" | "group";
};
type ExtendedFormValues = FormValues & { role: API.Member["role"] };

const initialValues: FormValues = {
    id: 0,
    email: "",
    target: "workspace",
};
const extendedInitialValues: ExtendedFormValues = {
    ...initialValues,
    role: "User",
};

const validationSchema = z.object({
    id: z.coerce.number(),
    email: z.email().max(50),
    target: z.enum(["group", "workspace"]),
});
const extendedValidationSchema = validationSchema.extend({
    role: z.enum(["Owner", "Manager", "User"]),
});

const targetOptions = [
    { label: "Group", value: "group" },
    { label: "Workspace", value: "workspace" },
];
const roleOptions = [
    { label: "Owner", value: "Owner" },
    { label: "Manager", value: "Manager" },
    { label: "User", value: "User" },
];
const useStyles = makeStyles()((theme: Theme) => ({
    section: { paddingBottom: 0 },
    formBox: { display: "flex", flexDirection: "column", gap: theme.spacing(2), padding: theme.spacing(2, 0) },
    buttonBox: { display: "flex", justifyContent: "flex-end" },
}));

const AdminMembersPage = () => {
    const [isAdding, setIsAdding] = React.useState(false);
    const [isChanging, setIsChanging] = React.useState(false);
    const [isRemoving, setIsRemoving] = React.useState(false);

    const validate = useValidationSchema(validationSchema);
    const extendedValidate = useValidationSchema(extendedValidationSchema);

    const { classes } = useStyles();
    const dispatch = useDispatch();
    const { t } = useTranslation(["common", "pages"]);
    const [updateWorkspaceMembers] = useUpdateWorkspaceMembersMutation();

    const onAddMember = async (values: ExtendedFormValues, form: FormApi<ExtendedFormValues>) => {
        try {
            setIsAdding(true);

            if (values.target === "group") {
                await dispatch(updateGroupMembers({ id: values.id, addedMembers: { [values.email]: values.role } })).unwrap();
            }
            if (values.target === "workspace") {
                await updateWorkspaceMembers({ id: values.id, addedMembers: { [values.email]: values.role } });
            }
            showSuccessToast("Member added successfully");
            form.restart(extendedInitialValues);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || "Something went wrong while adding the member");
        } finally {
            setIsAdding(false);
        }
    };
    const onChangeMember = async (values: ExtendedFormValues, form: FormApi<ExtendedFormValues>) => {
        try {
            setIsChanging(true);

            if (values.target === "group") {
                await dispatch(updateGroupMembers({ id: values.id, changedMembers: { [values.email]: values.role } })).unwrap();
            }
            if (values.target === "workspace") {
                await updateWorkspaceMembers({ id: values.id, changedMembers: { [values.email]: values.role } });
            }
            showSuccessToast("Member changed successfully");
            form.restart(extendedInitialValues);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || "Something went wrong while changing the member");
        } finally {
            setIsChanging(false);
        }
    };
    const onRemoveMember = async (values: FormValues, form: FormApi<FormValues>) => {
        try {
            setIsRemoving(true);

            if (values.target === "group") {
                await dispatch(updateGroupMembers({ id: values.id, removedMembers: [values.email] })).unwrap();
            }
            if (values.target === "workspace") {
                await updateWorkspaceMembers({ id: values.id, removedMembers: [values.email] });
            }
            showSuccessToast("Member removed successfully");
            form.restart(initialValues);
        } catch (error) {
            const err = error as Error;
            showErrorToast(err.message || "Something went wrong while removing the member");
        } finally {
            setIsRemoving(false);
        }
    };

    return (
        <PageContainer fullWidth={false}>
            <Form<ExtendedFormValues>
                onSubmit={onAddMember}
                initialValues={extendedInitialValues}
                validate={extendedValidate}
                render={({ handleSubmit, pristine }) => (
                    <form onSubmit={handleSubmit}>
                        <SectionContainer className={classes.section}>
                            <SectionHeader>{t("pages:adminMembersPage.addMember")}</SectionHeader>
                            <Box className={classes.formBox}>
                                <ControlledSelectField name="target" label="Target" options={targetOptions} />
                                <NumberField name="id" label="Target Id" />
                                <ControlledSelectField name="role" label="User role" options={roleOptions} disableSorting />
                                <TextField name="email" label="User email" />
                            </Box>
                            <Box className={classes.buttonBox}>
                                <PrimaryButton type="submit" disabled={pristine || isAdding} loading={isAdding}>
                                    {t("common:button.add")}
                                </PrimaryButton>
                            </Box>
                        </SectionContainer>
                    </form>
                )}
            />
            <Form<ExtendedFormValues>
                onSubmit={onChangeMember}
                initialValues={extendedInitialValues}
                validate={extendedValidate}
                render={({ handleSubmit, pristine }) => (
                    <form onSubmit={handleSubmit}>
                        <SectionContainer className={classes.section}>
                            <SectionHeader>{t("pages:adminMembersPage.changeMember")}</SectionHeader>
                            <Box className={classes.formBox}>
                                <ControlledSelectField name="target" label="Target" options={targetOptions} />
                                <NumberField name="id" label="Target id" />
                                <ControlledSelectField name="role" label="User role" options={roleOptions} disableSorting />
                                <TextField name="email" label="User email" />
                            </Box>
                            <Box className={classes.buttonBox}>
                                <PrimaryButton type="submit" disabled={pristine || isChanging} loading={isChanging}>
                                    {t("common:button.change")}
                                </PrimaryButton>
                            </Box>
                        </SectionContainer>
                    </form>
                )}
            />
            <Form<FormValues>
                onSubmit={onRemoveMember}
                initialValues={initialValues}
                validate={validate}
                render={({ handleSubmit, pristine }) => (
                    <form onSubmit={handleSubmit}>
                        <SectionContainer className={classes.section}>
                            <SectionHeader>{t("pages:adminMembersPage.removeMember")}</SectionHeader>
                            <Box className={classes.formBox}>
                                <ControlledSelectField name="target" label="Target" options={targetOptions} />
                                <NumberField name="id" label="Target id" />
                                <TextField name="email" label="User email" />
                            </Box>
                            <Box className={classes.buttonBox}>
                                <DangerButton type="submit" disabled={pristine || isRemoving} loading={isRemoving}>
                                    {t("common:button.remove")}
                                </DangerButton>
                            </Box>
                        </SectionContainer>
                    </form>
                )}
            />
        </PageContainer>
    );
};
export default withRequiredRole(
    withMetaDescription(AdminMembersPage, {
        title: i18next.t("pages:adminMembersPage.metaTitle"),
        description: i18next.t("pages:adminMembersPage.metaDescription"),
    }),
    [EmteriaRoles.EmteriaAdminRole],
);
